import {
  Alert,
  Group,
  ModalProps,
  MultiSelect,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core"
import { DefaultModal } from "../../../../-components/default-modal"
import { Form } from "../../../../-components/typed-form"
import { useTranslation } from "react-i18next"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import {
  CreateSearchNameRequest,
  GetPersonsWithSameNameRequest,
} from "../../../../@client/Theasoft_ApiServer_Persons"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../utils/form-submission-notifications"
import { FormButtonGroup } from "../../../../-components/form-button-group"
import { CancelButton, CreateButton } from "../../../../-components/Buttons"
import { useGetAuthenticatedUser } from "../../-components/use-authenticated-user"
import { SuspenseWithFallback } from "../../../../-components/suspense"
import { IconCheckBox } from "../../-components/icon-check-box"
import { useDebouncedValue } from "@mantine/hooks"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { ScrollContainer } from "../../../../-components/scroll-container"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"
import { useQueryClient } from "@tanstack/react-query"
import { getPaginatedPersons } from "@/orval/gen"

interface Props extends ModalProps {
  onPersonCreated?: (
    personId: number,
    firstName: string,
    lastName: string
  ) => void
  onPersonSelected?: (personId: number) => void
}

function CreatePersonModalContent({ ...props }: Props) {
  const { t } = useTranslation("app_server")
  const { data: categories } = useSuspenseQueryGet(
    "/api/PersonCategory/GetPersonCategories",
    null
  )
  const { data: salutations } = useSuspenseQueryGet(
    "/api/Salutation/GetSalutations",
    null
  )
  const defaultSalutation = salutations.at(0)
  if (!defaultSalutation) {
    return <Alert c={"red"}>No salutations found</Alert>
  }
  const { data: authenticatedUser } = useGetAuthenticatedUser()
  const { data: ownerGroupsResponse } = useSuspenseQueryGet(
    "/api/OwnerGroup/GetSimpleOwnerGroupsByPermissionArea",
    {
      permissionArea: "Kontaktdaten",
      onlyWithWritePermission: true,
    }
  )
  const ownerGroups = Array.isArray(ownerGroupsResponse)
    ? ownerGroupsResponse
    : []
  const defaultOwnergroup =
    ownerGroups.find(
      (g) => g.ownerGroupId === authenticatedUser.primaryOwnerGroupId
    ) ?? ownerGroups.at(0)
  if (!defaultOwnergroup) {
    return <Alert c={"red"}>No permitted owner groups found</Alert>
  }
  const { xs } = useBreakpoints()
  const queryClient = useQueryClient()
  return (
    <Form
      url="/api/Person/CreatePerson"
      initialValues={{
        firstName: "",
        lastName: "",
        keyName: "",
        categoryIds: [],
        ownerGroupId: defaultOwnergroup.ownerGroupId,
        salutationId: defaultSalutation.salutationId,
      }}
      transform={(values) => values}
      options={{
        onSuccess: (personId, variables) => {
          queryClient.invalidateQueries({
            queryKey: [getPaginatedPersons.name],
          })
          showSuccessNotification(t("PersonCreatedSuccessfully"))
          if (props.onPersonCreated && personId) {
            props.onPersonCreated(
              personId,
              variables.firstName,
              variables.lastName
            )
          }
          props.onClose()
        },
        onError: showErrorNotification,
      }}
    >
      {(form, _) => {
        const salutation = salutations.find(
          (s) => s.salutationId === form.values.salutationId
        )
        const isCompany = salutation?.salutationType === "Company"
        const [createSearchNameRequest] =
          useDebouncedValue<CreateSearchNameRequest>(
            {
              firstName: form.values.firstName,
              lastName: form.values.lastName,
              salutationType: salutation?.salutationType ?? "Undefined",
            },
            1000
          )
        const { data: searchName } = useQueryGet(
          "/api/Person/GetSearchName",
          createSearchNameRequest,
          {
            enabled:
              (!!form.values.firstName || !!form.values.lastName) &&
              !!salutation,
          }
        )
        React.useEffect(() => {
          form.setFieldValue(_.keyName._PATH_, searchName)
        }, [searchName])
        return (
          <Group wrap={xs ? "wrap" : "nowrap"} align="flex-start" gap="xl">
            <Stack w={xs ? "100%" : 400} gap="md">
              <MultiSelect
                required
                label={t("Category")}
                data={categories.map((c) => ({
                  value: c.personCategoryId.toString(),
                  label: c.name,
                }))}
                value={form.values.categoryIds.map(String)}
                onChange={(value) =>
                  form.setFieldValue(_.categoryIds._PATH_, value.map(Number))
                }
              />
              <Select
                required
                label={t("Salutation")}
                data={salutations
                  .filter((s) => s.name)
                  .map((s) => ({
                    value: s.salutationId.toString(),
                    label: s.name,
                  }))}
                value={form.values.salutationId?.toString()}
                onChange={(value) =>
                  form.setFieldValue(
                    _.salutationId._PATH_,
                    value ? Number(value) : null
                  )
                }
              />
              <TextInput
                required={!form.values.firstName || isCompany}
                maxLength={100}
                label={isCompany ? t("Name") : t("LastName")}
                {...form.getInputProps(_.lastName._PATH_)}
              />
              <TextInput
                required={!form.values.lastName}
                maxLength={100}
                label={isCompany ? t("Name") + " 2" : t("FirstName")}
                {...form.getInputProps(_.firstName._PATH_)}
              />
              <TextInput
                maxLength={20}
                label={t("KeyName")}
                {...form.getInputProps(_.keyName._PATH_)}
              />
              <Select
                label={t("Owner")}
                data={ownerGroups.map((g) => ({
                  value: g.ownerGroupId.toString(),
                  label: g.name,
                }))}
                value={form.values.ownerGroupId?.toString()}
                onChange={(value) =>
                  form.setFieldValue(
                    _.ownerGroupId._PATH_,
                    value ? Number(value) : null
                  )
                }
                required
              />
              <FormButtonGroup>
                <CancelButton onClick={props.onClose} />
                <CreateButton />
              </FormButtonGroup>
            </Stack>
            <SuspenseWithFallback>
              <PersonList
                firstName={form.values.firstName}
                lastName={form.values.lastName}
                onPersonSelected={props.onPersonSelected}
              />
            </SuspenseWithFallback>
          </Group>
        )
      }}
    </Form>
  )
}

function PersonList({
  firstName,
  lastName,
  onPersonSelected,
}: {
  firstName: string
  lastName: string
  onPersonSelected?: (personId: number) => void
}) {
  const [request] = useDebouncedValue<GetPersonsWithSameNameRequest>(
    { firstName, lastName },
    1000
  )

  const { data: persons } = useQueryGet(
    "/api/Person/GetPersonsWithSameName",
    request,
    { enabled: !!firstName || !!lastName }
  )
  const { t } = useTranslation("app_server")
  return (
    <Stack w={400} gap="md">
      <Text fz={"lg"}>{t("PersonsWithSameName")}</Text>
      <ScrollContainer mah={400}>
        <Table stickyHeader width={"100%"} h={"100%"}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("Name")}</Table.Th>
              <Table.Th>{t("City")}</Table.Th>
              <Table.Th>{t("Owner")}</Table.Th>
              <Table.Th>{t("Inactive")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {persons?.map((person) => {
              const fullNameWithSalutation =
                `${person.salutation?.name || ""} ${person.firstName} ${person.lastName}`.trim()
              return (
                <Table.Tr key={person.personId}>
                  <Table.Td>
                    <span
                      style={{
                        cursor: onPersonSelected ? "pointer" : undefined,
                        textDecoration: onPersonSelected
                          ? "underline"
                          : undefined,
                      }}
                      onClick={() =>
                        onPersonSelected && onPersonSelected(person.personId)
                      }
                    >
                      {fullNameWithSalutation}
                    </span>
                  </Table.Td>
                  <Table.Td>{person.firstAdress?.city}</Table.Td>
                  <Table.Td>{person.ownerGroup}</Table.Td>
                  <Table.Td>
                    <IconCheckBox checked={person.isInactive} />
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </ScrollContainer>
    </Stack>
  )
}

export function CreatePersonModal(props: Props) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal size={"xl"} {...props} title={t("CreatePerson")}>
      <CreatePersonModalContent {...props} />
    </DefaultModal>
  )
}
